import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, Input, Modal, notification, Select, Space, Table, Upload } from 'antd'
import { useSelector } from 'react-redux'

import SelectExam from '../../components/select-exam/SelectExam'
import {
  clearInterviewExaminees,
  deleteImportInterviewExaminees,
  downloadInterviewTemplate,
  examineeInterviewList,
  generateInterviewTable,
  getExamProcessTime,
  getInterviewPrintStatusFile,
  updateExamineesInterview,
  uploadExamineesInterviewPhoto
} from '../../common/request'
import ResponseCode from '../../common/response-code'
import router from '../../routers/router'
import ExamineeInterviewAdmissionModal from './ExamineeInterviewAdmissionModal'
import EditImportInterviewModal from './EditImportInterviewModal'
import ComponentVisibleByAuth from '../../components/ComponentVisibleByAuth'
import { downloadFile, throttle } from '../../plugins/utils'
import AppButton from '../../components/AppButton'
import PageStatus from '../../components/PageStatus'
import DeIdentification from '../../components/DeIdentification'
import PrintInterviewTicketModal from '../../components/PrintInterviewTicketModal'
import {
  DELETE_CONFIRM_CONTENT,
  isInterviewDeleteDisabled,
  PHOTO_STATUS_OPTIONS
} from './interviewImportConstants'

function ImportExamInterview () {
  const printInterviewTicketModal = useRef(null)
  const examineeInterviewAdmissionModalRef = useRef()
  const editImportInterviewModalRef = useRef()
  const examIdRef = useRef()
  const selectedExam = useSelector(state => state.examInfo.selectedExam)
  examIdRef.current = selectedExam?.id
  const [form] = Form.useForm()
  const [tableData, setTableData] = useState({})
  const [pageNum, setPageNum] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [currentEditItem, setCurrentEditItem] = useState({})
  const [pageStatus, setPageStatus] = useState({ text: '', status: '' })
  const [selectedTableItem, setSelectedTableItem] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [examProgressStatus, setExamProgressStatus] = useState()

  const deleteDisabled = isInterviewDeleteDisabled(examProgressStatus)

  const printInterviewOptions = [
    { label: '全部', value: '' },
    { label: '未打印', value: 0 },
    { label: '已打印', value: 1 }
  ]

  useEffect(() => {
    if (selectedExam.id) {
      form.resetFields()
      setPageNum(1)
      setSelectedRowKeys([])
      setSelectedTableItem([])
      fetchExamProgressStatus()
    }
  }, [selectedExam])

  useEffect(() => {
    if (selectedExam.id) {
      getTableData()
    }
  }, [selectedExam, pageNum, pageSize])

  const fetchExamProgressStatus = () => {
    getExamProcessTime({ id: selectedExam.id }).then(res => {
      if (res.code === ResponseCode.success) {
        setExamProgressStatus(res.data?.examinationStatus)
      }
    }).catch(err => {
      console.error(err)
    })
  }

  const buildQueryParams = () => {
    const searchData = form.getFieldsValue()
    const params = {
      ...searchData,
      examId: selectedExam.id,
      orderBy: '_id',
      orderType: 'DESC',
      pageSize,
      pageNum
    }

    if (searchData.photoStatus === '' || searchData.photoStatus == null) {
      delete params.photoStatus
    } else {
      params.photoStatus = Number(searchData.photoStatus)
    }

    return params
  }

  const getTableData = () => {
    if (!selectedExam.id) {
      return
    }

    setPageStatus({ text: '加载中...', status: 'loading' })

    examineeInterviewList(buildQueryParams()).then(res => {
      setPageStatus({ text: '', status: '' })
      if (res.code === ResponseCode.success) {
        setTableData(res.data)
      } else {
        Modal.error({
          title: '错误',
          content: res.msg
        })
      }
    }).catch(err => {
      setPageStatus({ text: '', status: '' })
      console.error(err)
      Modal.error({
        title: '错误',
        content: '网络错误，查询面试通知失败'
      })
    })
  }

  const showExamineeInterviewTableModal = (row) => {
    setCurrentEditItem(row)
    examineeInterviewAdmissionModalRef.current.showModal()
  }

  const showEditImportInterviewModal = (row) => {
    editImportInterviewModalRef.current.showModal(row)
  }

  const clearSelection = () => {
    setSelectedRowKeys([])
    setSelectedTableItem([])
  }

  const afterDeleteSuccess = (deletedCount) => {
    notification.success({
      message: '数据删除成功'
    })
    clearSelection()
    if ((tableData.elements?.length || 0) <= deletedCount && pageNum > 1) {
      setPageNum(pageNum - 1)
    } else {
      getTableData()
    }
  }

  const handleDelete = (ids) => {
    if (deleteDisabled) {
      return
    }

    const examId = examIdRef.current
    if (!examId) {
      Modal.error({
        title: '错误',
        content: '未选择考试，无法删除'
      })
      return
    }

    Modal.confirm({
      title: '确认删除',
      content: DELETE_CONFIRM_CONTENT,
      onOk: () => {
        return deleteImportInterviewExaminees({
          examId,
          ids
        }).then(res => {
          if (res.code === ResponseCode.success) {
            afterDeleteSuccess(ids.length)
          } else {
            Modal.error({
              title: '错误',
              content: res.msg || '数据删除失败'
            })
          }
        }).catch(err => {
          console.error(err)
          Modal.error({
            title: '错误',
            content: '网络错误，删除失败'
          })
        })
      }
    })
  }

  const handleBatchDelete = () => {
    if (deleteDisabled) {
      return
    }

    if (selectedTableItem.length === 0) {
      Modal.error({
        title: '错误',
        content: '请选择要删除的数据'
      })
      return
    }

    handleDelete(selectedTableItem.map(item => item._id))
  }

  const paginationChange = (num, size) => {
    setPageSize(size)
    if (size === pageSize) {
      setPageNum(num)
    } else {
      setPageNum(1)
    }
  }

  const handleSearch = () => {
    setPageNum(1)
    getTableData()
  }

  const handleReset = () => {
    form.resetFields()
    setPageNum(1)
    getTableData()
  }

  const beforeUpload = () => {
    setPageStatus({ text: '正在导入', status: 'loading' })
  }

  const uploadPersonImageChange = ({ file }) => {
    const res = file.response
    if (res) {
      setPageStatus({ text: '', status: '' })
      if (res.code === ResponseCode.success) {
        getTableData()
        Modal.success({
          title: '操作成功',
          content: res.msg
        })
      } else {
        Modal.error({
          title: '错误',
          content: res.msg
        })
      }
    }
  }

  const downloadInterviewTemplateAction = () => {
    setPageStatus({ text: '正在上传', status: 'loading' })
    downloadInterviewTemplate({ examId: selectedExam.id }).then(res => {
      setPageStatus({ text: '', status: '' })
      let blob = new Blob([res], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; application/octet-stream'
      })
      let objectUrl = URL.createObjectURL(blob)
      let a = document.createElement('a')
      a.href = objectUrl
      a.download = '面试准考证模板'
      a.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
      window.URL.revokeObjectURL(blob)
    }).catch(err => {
      setPageStatus({ text: '', status: '' })
      console.error(err)
      Modal.error({
        title: '错误',
        content: '网络错误，下载面试准考证模板失败'
      })
    })
  }

  const showClearInterviewDataModal = () => {
    const examId = examIdRef.current
    if (!examId) {
      Modal.error({
        title: '错误',
        content: '未选择考试，无法清除数据'
      })
      return
    }

    Modal.confirm({
      title: '清除数据',
      content: '确定要清除面试通知所有考生数据吗？',
      onOk: () => {
        clearInterviewExamineesAction(examId)
      }
    })
  }

  const clearInterviewExamineesAction = (examId) => {
    clearInterviewExaminees({ examId }).then(res => {
      if (res.code === ResponseCode.success) {
        notification.success({
          message: '操作成功'
        })
        getTableData()
      } else {
        Modal.error({
          title: '错误',
          content: res.msg
        })
      }
    }).catch(err => {
      console.error(err)
      Modal.error({
        title: '错误',
        content: '网络错误，清除面试通知考生数据失败'
      })
    })
  }

  const generateInterviewTableAction = (drawNumber) => {
    setPageStatus({ text: '正在生成', status: 'loading' })
    generateInterviewTable({ examId: selectedExam.id, drawNumber: drawNumber }).then(res => {
      setPageStatus({ text: '', status: '' })
      if (res.code === ResponseCode.success) {
        Modal.confirm({
          title: '操作成功',
          content: '下载任务已添加，是否跳转到下载中心查看',
          onOk: () => {
            const siteId = sessionStorage.getItem('siteId')
            router.navigate(`/${ siteId }/exam/download-center`)
          }
        })
      }
    }).catch(err => {
      console.error(err)
      setPageStatus({ text: '', status: '' })
      Modal.error({
        title: '错误',
        content: '网络错误，生成座次表失败'
      })
    })
  }

  const downloadPrintStatusFile = () => {
    const searchData = form.getFieldsValue()

    getInterviewPrintStatusFile({
      examId: selectedExam.id,
      ...searchData
    }).then(res => {
      downloadFile(res, 'xlsx', '考生面试成绩打印状态表').catch(err => {
        console.error(err)
        Modal.error({
          title: '错误',
          content: err,
        })
      })
    }).catch(err => {
      console.error(err)
      Modal.error({
        title: '错误',
        content: '网络错误，导出打印状态表失败'
      })
    })
  }

  const printBatch = () => {
    if (selectedTableItem.length === 0) {
      Modal.error({
        title: '错误',
        content: '未选择考生'
      })
      return
    }

    if (selectedTableItem.length > 500) {
      Modal.error({
        title: '错误',
        content: '单次打印数量不能超过500张'
      })
      return
    }

    printInterviewTicketModal.current.showModal(selectedTableItem)
  }

  const columns = useMemo(() => [
    {
      title: '考生编号',
      key: 'candidateNumber',
      dataIndex: 'candidateNumber'
    },
    {
      width: 100,
      title: '姓名',
      key: 'name',
      dataIndex: 'name',
      render: (record) => {
        return (<DeIdentification value={ record } type={ 'name' }></DeIdentification>)
      }
    },
    {
      title: '身份证号',
      key: 'idCard',
      dataIndex: 'idCard',
      render: (record) => {
        return (<DeIdentification value={ record } type={ 'idCard' }></DeIdentification>)
      }
    },
    {
      title: '单位代码',
      key: 'unitCode',
      dataIndex: 'unitCode'
    },
    {
      title: '报考单位',
      key: 'unitName',
      dataIndex: 'unitName'
    },
    {
      title: '报考职位',
      key: 'positionName',
      dataIndex: 'positionName'
    },
    {
      title: '职位代码',
      key: 'positionCode',
      dataIndex: 'positionCode'
    },
    {
      title: '打印状态',
      key: 'interviewPrintNumber',
      dataIndex: 'interviewPrintNumber',
      render: (record) => {
        return record && record > 0 ? '已打印' : '未打印'
      }
    },
    {
      title: '操作',
      width: 240,
      key: 'operation',
      dataIndex: 'operation',
      render: (_, row) => {
        return (
          <Space className="d-flex justify-content-end" size={ 4 } wrap>
            <Button
              type="primary"
              ghost
              size="small"
              onClick={ () => showExamineeInterviewTableModal(row) }
            >查看详情</Button>
            <Button
              type="primary"
              ghost
              size="small"
              onClick={ () => showEditImportInterviewModal(row) }
            >编辑</Button>
            <Button
              type="primary"
              ghost
              danger
              size="small"
              disabled={ deleteDisabled }
              onClick={ () => handleDelete([row._id]) }
            >删除</Button>
          </Space>
        )
      }
    }
  ], [deleteDisabled, selectedExam.id])

  return (<>
    <SelectExam>
      <PageStatus pageStatus={ pageStatus.status } text={ pageStatus.text }>
        <Form
          layout={ 'inline' }
          form={ form }
          initialValues={ {
            name: '',
            idCard: '',
            printInterview: '',
            photoStatus: ''
          } }
          onValuesChange={ throttle(handleSearch, 500) }
        >
          <Form.Item
            label={ '考生姓名' }
            name={ 'name' }
            trigger={ 'onInput' }
          >
            <Input placeholder={ '输入考生姓名查询' }></Input>
          </Form.Item>
          <Form.Item
            label={ '身份证号' }
            name={ 'idCard' }
            trigger={ 'onInput' }
          >
            <Input placeholder={ '输入考生身份证号查询' }></Input>
          </Form.Item>
          <Form.Item
            label={ '打印状态' }
            name={ 'printInterview' }
          >
            <Select options={ printInterviewOptions } style={ { width: 120 } }></Select>
          </Form.Item>
          <Form.Item
            label={ '照片筛选' }
            name={ 'photoStatus' }
          >
            <Select options={ PHOTO_STATUS_OPTIONS } style={ { width: 120 } }></Select>
          </Form.Item>
          <Form.Item>
            <Button onClick={ handleReset }>重置</Button>
          </Form.Item>
        </Form>
        <div className={ 'my-3 d-flex align-items-center justify-content-between' }>
          {
            selectedExam.examinationStatus < 4
              ? (
                <Space wrap>
                  <AppButton
                    authCode={ 'button_mssjdr-xzmb' }
                    type={ 'primary' }
                    onClick={ downloadInterviewTemplateAction }
                  >下载模板</AppButton>
                  <ComponentVisibleByAuth authCode={ 'button_mssjdr-drks' }>
                    <Upload
                      name={ 'file' }
                      action={ updateExamineesInterview }
                      headers={ {
                        authorization: window.sessionStorage.getItem('access_token')
                      } }
                      data={ {
                        examId: selectedExam.id
                      } }
                      disabled={ pageStatus.status === 'loading' }
                      maxCount={ 1 }
                      showUploadList={ false }
                      beforeUpload={ beforeUpload }
                      onChange={ uploadPersonImageChange }
                    >
                      <Button type={ 'primary' }>导入考生</Button>
                    </Upload>
                  </ComponentVisibleByAuth>
                  <ComponentVisibleByAuth authCode={ 'button_mssjdr-drzp' }>
                    <Upload
                      name={ 'file' }
                      action={ uploadExamineesInterviewPhoto }
                      headers={ {
                        authorization: window.sessionStorage.getItem('access_token')
                      } }
                      data={ {
                        examId: selectedExam.id
                      } }
                      disabled={ pageStatus.status === 'loading' }
                      maxCount={ 1 }
                      showUploadList={ false }
                      beforeUpload={ beforeUpload }
                      onChange={ uploadPersonImageChange }
                    >
                      <Button type={ 'primary' }>导入照片</Button>
                    </Upload>
                  </ComponentVisibleByAuth>
                  <AppButton
                    authCode={ 'button_mssjdr-qcsj' }
                    type={ 'primary' }
                    ghost
                    danger
                    onClick={ showClearInterviewDataModal }
                  >清除数据</AppButton>
                  <Button
                    danger
                    disabled={ deleteDisabled || selectedRowKeys.length === 0 }
                    onClick={ handleBatchDelete }
                  >批量删除</Button>
                </Space>
              )
              : (
                <Space wrap>
                  <Button
                    danger
                    disabled={ deleteDisabled || selectedRowKeys.length === 0 }
                    onClick={ handleBatchDelete }
                  >批量删除</Button>
                </Space>
              )
          }


          <Space wrap>
            <AppButton authCode={ 'button_mssjdr-plxz' } type={ 'primary' } onClick={ printBatch }>批量下载</AppButton>
            <AppButton
              authCode={ 'button_mssjdr-dcdyztb' }
              type={ 'primary' }
              onClick={ downloadPrintStatusFile }
            >导出打印状态表</AppButton>
            <AppButton
              authCode={ 'button_mssjdr-sczcb' }
              type={ 'primary' }
              onClick={ generateInterviewTableAction.bind(this, 0) }
            >座次表</AppButton>
            <AppButton
              authCode={ 'button_mssjdr-sczcb' }
              type={ 'primary' }
              onClick={ generateInterviewTableAction.bind(this, 1) }
            >抽签号座次表</AppButton>
          </Space>
        </div>

        <Table
          loading={ pageStatus.status === 'loading' }
          columns={ columns }
          dataSource={ tableData.elements }
          rowKey={ record => record._id }
          rowSelection={ {
            type: 'checkbox',
            selectedRowKeys,
            onChange: (keys, selectedRows) => {
              setSelectedRowKeys(keys)
              setSelectedTableItem(selectedRows)
            }
          } }
          pagination={ {
            showTotal: (total) => {
              return `共 ${ total } 条`
            },
            size: 'normal',
            showSizeChanger: true,
            total: tableData.totalElements,
            pageSize: pageSize,
            current: pageNum,
            onChange: paginationChange
          } }
        ></Table>
      </PageStatus>
    </SelectExam>

    <ExamineeInterviewAdmissionModal
      ref={ examineeInterviewAdmissionModalRef }
      examineeItem={ currentEditItem }
    ></ExamineeInterviewAdmissionModal>

    <EditImportInterviewModal
      ref={ editImportInterviewModalRef }
      onSuccess={ getTableData }
    ></EditImportInterviewModal>

    <PrintInterviewTicketModal ref={ printInterviewTicketModal }></PrintInterviewTicketModal>
  </>)
}

export default ImportExamInterview
